import {ApplicationRef, Binding, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, outputBinding, signal, Type, WritableSignal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {PanelEntry} from '../../models/panel-entry.model';
import {AppService} from '../app/app.service';
import {PanelLocationEnum} from '../../enums/panel-location.enum';
import {SentryService} from '../sentry/sentry.service';

@Injectable({
    providedIn: 'root',
})
export class PanelService {
    private readonly environmentInjector = inject(EnvironmentInjector);
    private readonly appRef = inject(ApplicationRef);
    private readonly appService = inject(AppService);
    private readonly router = inject(Router);
    private readonly sentryService = inject(SentryService);
    private readonly panelEntries: WritableSignal<ReadonlyArray<PanelEntry>> = signal([]);
    // Several hosts can exist for the same location at once, because when views are swapped
    // (tab change, overlay, route change) the new host registers before the old one is
    // destroyed. The host registered last is the active one; when it goes away, the previous
    // one takes over again.
    private readonly hostElements = new Map<PanelLocationEnum, Array<HTMLElement>>();

    public constructor() {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => this.closeAll());
    }

    public registerHost(location: PanelLocationEnum, element: HTMLElement): void {
        const registeredElements = this.hostElements.get(location) ?? [];
        if (registeredElements.includes(element)) {
            return;
        }

        if (registeredElements.length > 0) {
            this.closePanel(location);
        }

        this.hostElements.set(location, [
            ...registeredElements,
            element
        ]);
    }

    public unregisterHost(location: PanelLocationEnum, element: HTMLElement): void {
        const registeredElements = this.hostElements.get(location);
        if (!registeredElements?.includes(element)) {
            return;
        }

        if (this.getHostElement(location) === element) {
            this.closePanel(location);
        }

        const remainingElements = registeredElements.filter(registeredElement => registeredElement !== element);
        if (remainingElements.length === 0) {
            this.hostElements.delete(location);
        } else {
            this.hostElements.set(location, remainingElements);
        }
    }

    public closeAll(): void {
        for (const panelEntry of this.panelEntries()) {
            this.destroyComponentRef(panelEntry.ref);
        }

        this.panelEntries.set([]);
    }

    public closePanel(location: PanelLocationEnum): void {
        const panelEntries = this.panelEntries()
            .filter(entry => entry.location === location);

        for (const panelEntry of panelEntries) {
            this.closeComponentRef(panelEntry.ref);
        }
    }

    public isPanelOpen(location: PanelLocationEnum): boolean {
        return this.panelEntries()
            .some(panelEntry => panelEntry.location === location);
    }

    public openPanel<C>(location: PanelLocationEnum, panelComponent: Type<C>, bindings: Array<Binding> = []): void {
        const panelContainer = this.getHostElement(location);
        if (!panelContainer) {
            this.sentryService.captureException(new Error(`Could not open the panel "${panelComponent.name}" because no panel host is registered for the location "${location}".`));
            return;
        }

        this.appService.setShowingSmallMenu(location, false);

        // Opening a panel that takes no bindings again would only destroy and recreate the same view and
        // discard whatever the user has already entered. Panels that take bindings must be recreated,
        // because the new bindings carry new values.
        if (bindings.length === 0 && this.isSamePanelOpen(location, panelComponent)) {
            return;
        }

        this.closePanel(location);

        const componentRef = createComponent(panelComponent, {
            environmentInjector: this.environmentInjector,
            bindings: [
                ...bindings,
                outputBinding('panelClosed', () => {
                    this.closePanel(location);
                })
            ]
        });

        panelContainer.appendChild(componentRef.location.nativeElement);
        this.appRef.attachView(componentRef.hostView);
        componentRef.changeDetectorRef.detectChanges();
        this.panelEntries.update(panelEntries => [
            ...panelEntries,
            {
                location,
                panelComponent,
                ref: componentRef
            }
        ]);
    }

    private getHostElement(location: PanelLocationEnum): HTMLElement | undefined {
        const registeredElements = this.hostElements.get(location);
        return registeredElements?.[registeredElements.length - 1];
    }

    private isSamePanelOpen(location: PanelLocationEnum, panelComponent: Type<unknown>): boolean {
        return this.panelEntries()
            .some(panelEntry => panelEntry.location === location && panelEntry.panelComponent === panelComponent);
    }

    private closeComponentRef<C>(componentRef: ComponentRef<C>): void {
        this.destroyComponentRef(componentRef);
        this.panelEntries.update(panelEntries => panelEntries.filter(panelEntry => panelEntry.ref !== componentRef));
    }

    private destroyComponentRef<C>(componentRef: ComponentRef<C>): void {
        componentRef.location.nativeElement.remove();
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();
    }
}
